<script lang="ts">
    import { onDestroy } from "svelte";
    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import { BUTTON_ZOOM_FACTOR_PER_SECOND, BUTTON_ZOOM_STEP_FACTOR } from "../../Phaser/Game/CameraZoomUtils";
    import LL from "../../../i18n/i18n-svelte";
    import { IconMinus, IconPlus } from "@wa-icons";

    // Keep click and long-press as the same gesture: a press always starts with one smooth zoom step,
    // then turns into continuous zoom if the user keeps the pointer down.
    const ZOOM_HOLD_DELAY = 180;

    // Track one active pointer so a second finger/button cannot start a competing zoom loop.
    let zoomHoldTimeout: ReturnType<typeof setTimeout> | undefined;
    let activeZoomPointerId: number | undefined;

    function getCameraManager() {
        return gameManager.getCurrentGameScene().getCameraManager();
    }

    function clearZoomHoldTimeout() {
        if (zoomHoldTimeout === undefined) {
            return;
        }

        clearTimeout(zoomHoldTimeout);
        zoomHoldTimeout = undefined;
    }

    function isPointerEvent(event?: Event): event is PointerEvent {
        return event !== undefined && "pointerId" in event;
    }

    /**
     * Starts the zoom gesture for mouse, touch, and stylus.
     * Analytics is emitted once here so a long press does not create one event per animation frame.
     */
    function startZoom(direction: "in" | "out", event: PointerEvent) {
        if (event.pointerType === "mouse" && event.button !== 0) {
            return;
        }

        event.preventDefault();

        if (activeZoomPointerId !== undefined) {
            return;
        }

        activeZoomPointerId = event.pointerId;
        const cameraManager = gameManager.getCurrentGameScene().getCameraManager();
        const zoomFactor = direction === "in" ? BUTTON_ZOOM_STEP_FACTOR : 1 / BUTTON_ZOOM_STEP_FACTOR;
        const continuousZoomFactor =
            direction === "in" ? BUTTON_ZOOM_FACTOR_PER_SECOND : 1 / BUTTON_ZOOM_FACTOR_PER_SECOND;

        if (direction === "in") {
            analyticsClient.trackAdminEvent("map_explorer.zoom_in_clicked");
        } else {
            analyticsClient.trackAdminEvent("map_explorer.zoom_out_clicked");
        }

        cameraManager?.smoothZoomByFactor(zoomFactor);

        // If the pointer is still down after the delay, the same gesture becomes continuous zoom.
        clearZoomHoldTimeout();
        zoomHoldTimeout = setTimeout(() => {
            if (activeZoomPointerId === event.pointerId) {
                getCameraManager()?.startContinuousZoom(continuousZoomFactor);
            }
        }, ZOOM_HOLD_DELAY);
    }

    /**
     * Stops the gesture from all exit paths: release, cancel, leaving the button, window blur, or component destroy.
     */
    function stopZoom(event?: Event) {
        if (isPointerEvent(event) && activeZoomPointerId !== undefined && activeZoomPointerId !== event.pointerId) {
            return;
        }

        const hadActiveZoom = activeZoomPointerId !== undefined || zoomHoldTimeout !== undefined;
        clearZoomHoldTimeout();
        activeZoomPointerId = undefined;
        if (hadActiveZoom) {
            getCameraManager()?.stopContinuousZoom();
        }
    }

    onDestroy(() => {
        stopZoom();
    });
</script>

<svelte:window onblur={stopZoom} />

<div
    class="absolute bottom-3 pointer-events-auto bg-mlight/80 rounded-md2 shadow-light right-1 @md/main-layout:right-2 @xl/main-layout:right-6 hidden @[200px]/main-layout:block"
    data-testid="actions-explorer"
>
    <div class="flex flex-col justify-center">
        <div class="flex flex-col justify-center">
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
                class="zoomIn group flex justify-center items-center cursor-pointer rounded-t-md2 pt-1 w-11 @sm/actions:w-12 h-9 @sm/actions:h-10"
                onpointerdown={(event) => startZoom("in", event)}
                onpointerup={stopZoom}
                onpointercancel={stopZoom}
                onpointerleave={stopZoom}
                aria-label={$LL.mapEditor.explorer.zoomIn()}
            >
                <IconPlus class="text-sm @sm/main-layout:text-lg" />
                <div
                    class="right-14 translate-x-2 opacity-0 group-hover:opacity-90 group-hover:translate-x-0 absolute bg-mlight backdrop-blur text-sm px-2 py-1 rounded whitespace-nowrap transition-all text-mdark pointer-events-none select-none"
                >
                    {$LL.mapEditor.explorer.zoomIn()}
                </div>
            </div>
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
                class="zoomOut group flex justify-center items-center cursor-pointer rounded-b-md2 pb-1 w-11 @sm/actions:w-12 h-9 @sm/actions:h-10"
                onpointerdown={(event) => startZoom("out", event)}
                onpointerup={stopZoom}
                onpointercancel={stopZoom}
                onpointerleave={stopZoom}
                aria-label={$LL.mapEditor.explorer.zoomOut()}
            >
                <IconMinus class="text-sm @sm/main-layout:text-lg" />
                <div
                    class="right-14 translate-x-2 opacity-0 group-hover:opacity-90 group-hover:translate-x-0 absolute bg-mlight backdrop-blur text-sm px-2 py-1 rounded whitespace-nowrap transition-all text-mdark pointer-events-none select-none"
                >
                    {$LL.mapEditor.explorer.zoomOut()}
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="css">
    .zoomIn:hover,
    .zoomOut:hover {
        box-shadow:
            3px 3px 4px 0px #c7d5ff inset,
            -2px -2px 4px 0px #f4f7ff inset;
    }
</style>
